/**
 * Performance monitoring component
 * Displays real-time performance metrics and analytics
 */

import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/Card';
import Badge from './ui/Badge';
import Button from './ui/Button';
import { Activity, Zap, Clock, TrendingUp, AlertTriangle } from 'lucide-react';
import { optimizedApiService } from '../lib/apiOptimized';

interface PerformanceMetrics {
  [operation: string]: {
    count: number;
    totalTime: number;
    minTime: number;
    maxTime: number;
    errors: number;
    avgTime: number;
  };
}

interface PerformanceMonitorProps {
  isVisible?: boolean;
  onToggle?: () => void;
}

const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({ 
  isVisible = false, 
  onToggle 
}) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({});
  const [isExpanded, setIsExpanded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchMetrics = async () => {
    try {
      const performanceData = optimizedApiService.performance.metrics() as PerformanceMetrics;
      setMetrics(performanceData);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch performance metrics:', error);
    }
  };

  useEffect(() => {
    if (isVisible) {
      fetchMetrics();
      
      // Update metrics every 10 seconds
      const interval = setInterval(fetchMetrics, 10000);
      
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  const getPerformanceColor = (avgTime: number): string => {
    if (avgTime < 100) return 'text-green-600';
    if (avgTime < 500) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceBadge = (avgTime: number) => {
    if (avgTime < 100) return <Badge variant="success">Fast</Badge>;
    if (avgTime < 500) return <Badge variant="warning">Moderate</Badge>;
    return <Badge variant="error">Slow</Badge>;
  };

  const formatOperationName = (operation: string): string => {
    return operation
      .replace(/^GET\s+|^POST\s+|^PUT\s+|^DELETE\s+/i, '')
      .replace(/\/api\//, '')
      .replace(/\//g, ' / ')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const totalRequests = Object.values(metrics).reduce((sum, metric) => sum + metric.count, 0);
  const totalErrors = Object.values(metrics).reduce((sum, metric) => sum + metric.errors, 0);
  const avgResponseTime = totalRequests > 0 
    ? Object.values(metrics).reduce((sum, metric) => sum + metric.totalTime, 0) / totalRequests 
    : 0;

  if (!isVisible) {
    return null;
  }

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CardTitle className="text-lg">Performance Monitor</CardTitle>
            <Activity className="h-5 w-5 text-blue-500" />
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchMetrics}
              className="flex items-center space-x-1"
            >
              <Zap className="h-4 w-4" />
              <span>Refresh</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </Button>
            {onToggle && (
              <Button
                variant="outline"
                size="sm"
                onClick={onToggle}
              >
                Hide
              </Button>
            )}
          </div>
        </div>
        {lastUpdate && (
          <p className="text-sm text-gray-600">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </p>
        )}
      </CardHeader>
      
      {/* Summary Stats */}
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Total Requests</span>
            </div>
            <p className="text-2xl font-bold text-blue-900 mt-1">{totalRequests}</p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-800">Avg Response</span>
            </div>
            <p className="text-2xl font-bold text-green-900 mt-1">
              {avgResponseTime.toFixed(0)}ms
            </p>
          </div>
          
          <div className="bg-red-50 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium text-red-800">Errors</span>
            </div>
            <p className="text-2xl font-bold text-red-900 mt-1">{totalErrors}</p>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Activity className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-800">Endpoints</span>
            </div>
            <p className="text-2xl font-bold text-purple-900 mt-1">
              {Object.keys(metrics).length}
            </p>
          </div>
        </div>

        {/* Detailed Metrics */}
        {isExpanded && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Endpoint Performance</h3>
            {Object.entries(metrics).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No performance data available yet
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(metrics).map(([operation, metric]) => (
                  <div
                    key={operation}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {formatOperationName(operation)}
                        </p>
                        <p className="text-sm text-gray-600">
                          {metric.count} requests • {metric.errors} errors
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <p className={`text-lg font-bold ${getPerformanceColor(metric.avgTime)}`}>
                          {metric.avgTime.toFixed(0)}ms
                        </p>
                        <p className="text-xs text-gray-500">
                          {metric.minTime.toFixed(0)}-{metric.maxTime.toFixed(0)}ms
                        </p>
                      </div>
                      {getPerformanceBadge(metric.avgTime)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PerformanceMonitor;
